import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const GIFT_PROPERTY_KEY = '_gift_step';

const formatMoney = (cents) => {
  const value = Number(cents) / 100;
  if (!Number.isFinite(value)) return '';
  try {
    return value.toLocaleString(document.documentElement.lang || 'pt-BR', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'BRL',
    });
  } catch (e) {
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  }
};

const escapeHTML = (str) =>
  String(str ?? '').replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[ch]));

class CartProgressBar extends HTMLElement {
  #steps = [];
  #mode = 'value';
  #messageEl = null;
  #barsEl = null;
  #abortController = null;
  #fetchTimeout = null;
  #dialogObserver = null;
  #giftSyncing = false;
  #giftSyncPending = false;
  #collectionScoped = false;
  #onCartUpdate = (event) => {
    if (event?.detail?.data?.source === 'cart-progress-bar-gift') return;

    // When scoped to a collection, the measured total is computed server-side and exposed
    // via the data-collection-* attributes (kept fresh by the section morph on every cart
    // change). The cart.js-shaped event resource can't tell us collection membership, so we
    // wait a frame for the morph to apply, then re-read the attribute.
    if (this.#collectionScoped) {
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#update(this.#collectionTotal());
        this.#scheduleGiftSync(null);
      });
      return;
    }

    const resource = event?.detail?.resource;
    const hasCartShape = resource && typeof resource.total_price !== 'undefined' && typeof resource.item_count !== 'undefined';
    if (hasCartShape) {
      const total = this.#extractTotal(resource);
      // morphSection (component-cart-items.js) roda DEPOIS deste evento e zera
      // barras/mensagem no /cart; atualiza no quadro seguinte. (Claude 29-09, v35)
      requestAnimationFrame(() => { if (this.isConnected) this.#update(total); });
      this.#scheduleGiftSync(resource);
      return;
    }
    this.#scheduleFetchUpdate();
    this.#scheduleGiftSync(null);
  };

  static get observedAttributes() {
    return ['data-collection-subtotal', 'data-collection-qty'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    // The section morph updates these attributes in place after a cart change. React only
    // once connected/initialized and only when actually scoped to a collection.
    if (!this.#collectionScoped || !this.isConnected || !this.#steps.length) return;
    this.#update(this.#collectionTotal());
    this.#scheduleGiftSync(null);
  }

  connectedCallback() {
    this.#mode = this.getAttribute('data-mode') === 'quantity' ? 'quantity' : 'value';
    this.#messageEl = this.querySelector('[data-cart-progress-message]');
    this.#barsEl = this.querySelector('[data-cart-progress-bars]');

    try {
      const raw = this.getAttribute('data-steps') || '[]';
      this.#steps = JSON.parse(raw)
        .filter((s) => typeof s.threshold === 'number' && s.threshold > 0)
        .sort((a, b) => a.threshold - b.threshold);
    } catch (e) {
      this.#steps = [];
    }

    if (!this.#steps.length) {
      this.hidden = true;
      return;
    }

    this.#collectionScoped = this.getAttribute('data-collection-scoped') === 'true';

    this.#renderBars();

    const initial = this.#collectionScoped
      ? this.#collectionTotal()
      : parseInt(this.getAttribute('data-initial-total') || '0', 10) || 0;
    this.#update(initial);

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#watchDialogOpen();
    this.#scheduleGiftSync(null);
  }

  #watchDialogOpen() {
    const dialog = this.closest('dialog');
    if (!dialog) return;
    this.#dialogObserver = new MutationObserver(() => {
      if (dialog.hasAttribute('open')) {
        requestAnimationFrame(() => this.#refreshOnOpen());
      }
    });
    this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    if (dialog.hasAttribute('open')) {
      requestAnimationFrame(() => this.#refreshOnOpen());
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#fetchTimeout) {
      clearTimeout(this.#fetchTimeout);
      this.#fetchTimeout = null;
    }
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
  }

  #extractTotal(cart) {
    if (this.#mode === 'quantity') {
      return parseInt(cart?.item_count ?? 0, 10) || 0;
    }
    // [PB 04-09] O limiar mede o VALOR DOS PRODUTOS, antes de qualquer desconto.
    // Antes usava total_price, que ja vem com o cupom aplicado: a cliente cruzava os
    // R$ 799, via "voce ganhou a necessaire", aplicava um cupom e perdia o brinde --
    // o mesmo valia para o frete gratis de R$ 649. items_subtotal_price nao resolve,
    // porque o cupom entra como desconto de linha e ele tambem vem reduzido.
    // So original_total_price preserva o valor cheio dos produtos.
    return parseInt(cart?.original_total_price ?? cart?.total_price ?? 0, 10) || 0;
  }

  #collectionTotal() {
    if (this.#mode === 'quantity') {
      return parseInt(this.getAttribute('data-collection-qty') || '0', 10) || 0;
    }
    return parseInt(this.getAttribute('data-collection-subtotal') || '0', 10) || 0;
  }

  // Re-fetch the server-computed collection totals via the Section Rendering API. Used when the
  // component can't rely on a morph having happened (e.g. the drawer opening, or a cart change
  // made in another tab), since /cart.js doesn't expose collection membership.
  async #refreshCollectionFromSection() {
    const sectionId = this.getAttribute('data-section-id');
    if (sectionId) {
      try {
        const url = `${window.location.pathname}?sections=${encodeURIComponent(sectionId)}`;
        const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const html = data?.[sectionId];
          if (typeof html === 'string') {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const fresh = doc.querySelector('cart-progress-bar');
            if (fresh) {
              const sub = fresh.getAttribute('data-collection-subtotal');
              const qty = fresh.getAttribute('data-collection-qty');
              if (sub != null) this.setAttribute('data-collection-subtotal', sub);
              if (qty != null) this.setAttribute('data-collection-qty', qty);
            }
          }
        }
      } catch (e) {
        /* network error — fall back to whatever attribute we currently hold */
      }
    }
    if (!this.isConnected) return;
    this.#update(this.#collectionTotal());
    this.#scheduleGiftSync(null);
  }

  #scheduleFetchUpdate() {
    if (this.#fetchTimeout) clearTimeout(this.#fetchTimeout);
    this.#fetchTimeout = setTimeout(() => {
      this.#fetchTimeout = null;
      this.#refreshFromFetch();
    }, 50);
  }

  #refreshOnOpen() {
    if (this.#collectionScoped) {
      this.#refreshCollectionFromSection();
      return;
    }
    this.#refreshFromFetch();
  }

  async #refreshFromFetch() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        signal,
      });
      if (!res.ok || signal.aborted) return;
      const cart = await res.json();
      if (signal.aborted || !this.isConnected) return;
      this.#update(this.#extractTotal(cart));
    } catch (e) {
      /* aborted or network error */
    }
  }

  #renderBars() {
    if (!this.#barsEl) return;
    this.#barsEl.style.setProperty('--cart-progress-count', String(this.#steps.length));
    this.#barsEl.innerHTML = this.#steps
      .map(
        (step, index) => `
          <div class="cart-progress__step" data-step-index="${index}">
            <div class="cart-progress__track">
              <div class="cart-progress__fill" style="width:0%"></div>
            </div>
            <div class="cart-progress__label">${escapeHTML(step.title || '')}</div>
          </div>
        `
      )
      .join('');
  }

  #update(total) {
    if (!this.#steps.length) return;

    if (this.#barsEl && !document.contains(this.#barsEl)) {
      this.#barsEl = this.querySelector('[data-cart-progress-bars]');
    }
    if (this.#messageEl && !document.contains(this.#messageEl)) {
      this.#messageEl = this.querySelector('[data-cart-progress-message]');
    }

    if (this.#barsEl && this.#barsEl.children.length === 0) {
      this.#renderBars();
    }

    let nextIdx = -1;
    this.#steps.forEach((step, index) => {
      const prev = index === 0 ? 0 : this.#steps[index - 1].threshold;
      const span = Math.max(1, step.threshold - prev);
      const progress = Math.max(0, Math.min(1, (total - prev) / span));
      const stepEl = this.#barsEl?.querySelector(`[data-step-index="${index}"]`);
      const fill = stepEl?.querySelector('.cart-progress__fill');
      if (fill) fill.style.width = `${(progress * 100).toFixed(2)}%`;
      if (stepEl) stepEl.classList.toggle('is-achieved', total >= step.threshold);
      if (nextIdx === -1 && total < step.threshold) nextIdx = index;
    });

    if (!this.#messageEl) return;

    if (total <= 0) {
      this.#messageEl.textContent = this.getAttribute('data-empty-message') || '';
      this.#messageEl.classList.remove('is-complete');
      return;
    }

    if (nextIdx === -1) {
      this.#messageEl.textContent = this.getAttribute('data-complete-message') || '';
      this.#messageEl.classList.add('is-complete');
      return;
    }

    this.#messageEl.classList.remove('is-complete');
    const nextStep = this.#steps[nextIdx];
    const missing = Math.max(0, nextStep.threshold - total);
    const fallback = this.getAttribute('data-reward-fallback') || 'benefício';
    const rewardTitle = nextStep.title?.trim() || fallback;
    const reward = `<strong>${escapeHTML(rewardTitle)}</strong>`;

    if (this.#mode === 'quantity') {
      const template =
        missing === 1
          ? this.getAttribute('data-remaining-qty-template-one') || ''
          : this.getAttribute('data-remaining-qty-template-other') || '';
      this.#messageEl.innerHTML = template
        .replace('__count__', `<strong>${missing}</strong>`)
        .replace('__reward__', reward);
    } else {
      const template = this.getAttribute('data-remaining-value-template') || '';
      this.#messageEl.innerHTML = template
        .replace('__amount__', `<strong>${escapeHTML(formatMoney(missing))}</strong>`)
        .replace('__reward__', reward);
    }
  }

  #hasAnyGiftStep() {
    return this.#steps.some((s) => s.giftVariantId);
  }

  #scheduleGiftSync(cart) {
    if (!this.#hasAnyGiftStep()) return;
    if (this.#giftSyncing) {
      this.#giftSyncPending = true;
      return;
    }
    this.#giftSyncing = true;
    this.#syncGifts(cart)
      .catch(() => {})
      .finally(() => {
        this.#giftSyncing = false;
        if (this.#giftSyncPending) {
          this.#giftSyncPending = false;
          this.#scheduleGiftSync(null);
        }
      });
  }

  async #syncGifts(maybeCart) {
    let cart = maybeCart;
    if (!cart || !Array.isArray(cart.items)) {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!res.ok) return;
        cart = await res.json();
      } catch (e) {
        return;
      }
    }

    // The /cart.js items list is still used to find which gift items are present, but the
    // measured total comes from the server-computed collection attribute when scoped.
    const total = this.#collectionScoped ? this.#collectionTotal() : this.#extractTotal(cart);

    // [PB] Variant IDs configurados como brinde. Serve para reconhecer linhas do produto de
    // brinde que entraram no carrinho por fora deste componente — por exemplo, a cliente
    // abrindo a pagina do produto por link direto (ele e UNLISTED, nao bloqueado).
    const giftVariantIds = new Set(
      this.#steps
        .map((s) => Number(s.giftVariantId))
        .filter((id) => Number.isFinite(id) && id > 0)
    );

    const presentByStep = new Map();
    // [PB] Linhas de brinde ilegitimas: sem carimbo, ou segunda linha com o mesmo carimbo.
    const strayGiftItems = [];

    for (const item of cart.items) {
      const stepIdx = parseInt(item?.properties?.[GIFT_PROPERTY_KEY] ?? '', 10);
      if (Number.isFinite(stepIdx)) {
        if (presentByStep.has(stepIdx)) {
          strayGiftItems.push(item);
        } else {
          presentByStep.set(stepIdx, item);
        }
      } else if (giftVariantIds.has(Number(item?.variant_id))) {
        strayGiftItems.push(item);
      }
    }

    const stepsWithGifts = this.#steps
      .map((step, index) => ({ index, ...step }))
      .filter((s) => s.giftVariantId);

    const toRemove = [...strayGiftItems];
    const toAdd = [];
    // [PB] Linhas legitimas com quantidade acima de 1 — trava de maximo 1 brinde por pedido.
    const toClamp = [];

    for (const step of stepsWithGifts) {
      const isAchieved = total >= step.threshold;
      const present = presentByStep.get(step.index);
      if (isAchieved && !present) {
        toAdd.push(step);
      } else if (!isAchieved && present) {
        toRemove.push(present);
      } else if (isAchieved && present && Number(present.quantity) > 1) {
        toClamp.push(present);
      }
    }

    for (const [stepIdx, item] of presentByStep) {
      const stepExists = stepsWithGifts.some((s) => s.index === stepIdx);
      if (!stepExists && !toRemove.includes(item)) {
        toRemove.push(item);
      }
    }

    if (!toAdd.length && !toRemove.length && !toClamp.length) return;

    let didChange = false;
    let lastResponse = null;

    for (const item of toRemove) {
      const res = await this.#cartChange({ id: item.key, quantity: 0 });
      if (res) {
        didChange = true;
        lastResponse = res;
      }
    }

    // [PB] Maximo 1 brinde por pedido: linha carimbada com quantidade maior que 1 volta para 1
    // em vez de ser removida, para a cliente nao perder o beneficio a que tem direito.
    for (const item of toClamp) {
      const res = await this.#cartChange({ id: item.key, quantity: 1 });
      if (res) {
        didChange = true;
        lastResponse = res;
      }
    }

    for (const step of toAdd) {
      const res = await this.#cartAdd({
        id: Number(step.giftVariantId),
        quantity: 1,
        properties: { [GIFT_PROPERTY_KEY]: String(step.index) },
      });
      if (res) {
        didChange = true;
        lastResponse = res;
      }
    }

    if (didChange) {
      try {
        const freshRes = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const freshCart = freshRes.ok ? await freshRes.json() : null;
        document.dispatchEvent(
          new CartUpdateEvent(freshCart || lastResponse, this.id || 'cart-progress-bar', {
            source: 'cart-progress-bar-gift',
            sections: lastResponse?.sections,
          })
        );
        if (freshCart && this.isConnected) {
          this.#messageEl = this.querySelector('[data-cart-progress-message]');
          this.#barsEl = this.querySelector('[data-cart-progress-bars]');
          this.#update(this.#collectionScoped ? this.#collectionTotal() : this.#extractTotal(freshCart));
        }
      } catch (e) {
        /* ignore */
      }
    }
  }

  #getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }

  async #cartAdd({ id, quantity, properties }) {
    const sections = this.#getCartSectionIds();
    const payload = {
      items: [{ id, quantity, properties }],
    };
    if (sections.length) payload.sections = sections.join(',');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async #cartChange({ id, quantity }) {
    const sections = this.#getCartSectionIds();
    const payload = { id, quantity };
    if (sections.length) payload.sections = sections.join(',');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
